import { createElement, useEffect, useState, useCallback } from "react";
import axiosClient from "@service/axiosClient";
import { AgGridReact } from "ag-grid-react";
import { GridApi, GridReadyEvent } from "ag-grid-community";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import { FiSearch, FiEdit } from "react-icons/fi";
import { IoIosAddCircleOutline } from "react-icons/io";

import { PlantCreateEdit } from "@components/plant/PlantCreateEdit";
import { IPlantDTO } from "@interfaces/IPlantDTO";
import { Box } from "@mui/material";
import Input from "@components/Input";
import { PlantListMobile } from "@components/plant/PlantListMobile";
import RoleGroupService from "@service/RoleGroupService";
import { IRoleGroupDTO } from "@interfaces/IRoleGroupDTO";
import { ISelect } from "@interfaces/ISelect";
import { fetchEngagementStatus } from "@service/PlantService";
import { IEngagementStatusesDTO } from "@interfaces/IEngagementStatusesDTO";
import { AgGridContextMenu } from "@components/ag_grid/AgGridContextMenu";
import CompanyService from "@service/CompanyService";
import ClientService from "@service/ClientService";
import { ICompanyDTO } from "@interfaces/ICompanyDTO";
import { IClientDTO } from "@interfaces/IClientDTO";

export function PlantList(): JSX.Element {
  const [plants, setPlants] = useState<IPlantDTO[]>([]);
  const [selectedPlant, setSelectedPlant] = useState<IPlantDTO | null>(null);
  const [roleGroups, setRoleGroups] = useState<ISelect[]>([]);
  const [engagementStatuses, setEngagementStatuses] = useState<ISelect[]>([]);
  const [companies, setCompanies] = useState<ISelect[]>([]);
  const [clients, setClients] = useState<ISelect[]>([]);

  const [plant_id, setPlant_id] = useState<number>(-1);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const breakpoint = 768; // Define the breakpoint for mobile view
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint, isMobile]);

  useEffect(() => {
    async function fetchPlants() {
      setIsLoading(false);
      try {
        const response = await axiosClient.get("/plant_api/plants_list");
        setPlants(response.data);
        const fetchedRoleGroups = await RoleGroupService.getAllRoleGroups();
        setRoleGroups(
          (fetchedRoleGroups ?? []).map((roleGroup: IRoleGroupDTO) => ({
            id: roleGroup.id,
            value: roleGroup.name,
          }))
        );
        const engagementStatuses = await fetchEngagementStatus();
        setEngagementStatuses(
          (engagementStatuses ?? []).map(
            (engagement: IEngagementStatusesDTO) => ({
              id: engagement.id,
              value: engagement.name,
            })
          )
        );
        const fetchedCompanies = await CompanyService.getAllCompanies();
        setCompanies(
          (fetchedCompanies ?? [])
            .filter((company: ICompanyDTO) => company.id !== undefined)
            .map((company: ICompanyDTO) => ({
              id: company.id as number,
              value: company.name,
            }))
        );
        const fetchedClients = await ClientService.getAllClients();
        setClients(
          (fetchedClients ?? [])
            .filter((client: IClientDTO) => client.id !== undefined)
            .map((client: IClientDTO) => ({
              id: client.id as number,
              value: client.name,
            }))
        );
      } catch (error) {
        console.error("Error fetching plants:", error);
      }
    }
    if (isLoading) {
      fetchPlants();
    }
  }, [isLoading]);

  const pageSize = 13;
  const [gridApi, setGridApi] = useState<GridApi>();
  const defaultColDef = {
    sortable: true,
  };
  const [searchVal, setsearchVal] = useState("");

  // Context menu hook
  const { handleCellContextMenu, ContextMenuComponent } = AgGridContextMenu({});

  // Prevent default browser context menu
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  function onGridReady(params: GridReadyEvent): void {
    setGridApi(params.api);
  }

  const updateSearchQuery = (val: string): void => {
    if (gridApi !== undefined) {
      gridApi.setQuickFilter(val);
      setsearchVal(val);
    }
  };

  const handleEdit = (param: any): void => {
    if (param !== null) {
      setPlant_id(param.id);
      setSelectedPlant(param);
      setShowEditDialog(true);
    } else {
      setPlant_id(0);
      setSelectedPlant(null);
      setShowEditDialog(true);
    }
  };

  const handleClose = () => {
    setShowEditDialog(false);
    setSelectedPlant(null);
    setPlant_id(-1);
    setIsLoading(true);
  };

  const columnDefs = [
    {
      headerName: "Plant Id",
      field: "id",
      width: 150,
      filter: true,
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Plant Name",
      field: "name",
      width: 350,
      filter: true,
    },
    {
      headerName: "Plant Location",
      field: "location",
      width: 350,
      filter: true,
    },
    {
      headerName: "Plant Admin Fee %",
      field: "admin_fee",
      width: 200,
      valueFormatter: (params: any) => {
        if (typeof params.value === "number") {
          return `${(params.value * 100).toFixed(2)}%`; // Multiplies by 100 and adds '%'
        }
        return params.value; // Return original value if not a number
      },
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Edit",
      field: "action_buttons1",
      width: 65,
      filter: true,
      cellRenderer: (params: any): JSX.Element => {
        return (
          <button
            className="tw-mt-2 tw-mb-2 tw-color-primary"
            onClick={() => handleEdit(params.data)}
          >
            {createElement(FiEdit as React.ComponentType<{ size?: number }>, {
              size: 16,
            })}
          </button>
        );
      },
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Add",
      field: "action_buttons1",
      width: 65,
      filter: true,
      cellRenderer: (params: any): JSX.Element => {
        return (
          <button
            className="tw-mt-2 tw-mb-2 tw-color-primary"
            onClick={() => handleEdit(null)}
          >
            {createElement(
              IoIosAddCircleOutline as React.ComponentType<{ size?: number }>,
              { size: 16 }
            )}
          </button>
        );
      },
      cellStyle: { textAlign: "center" },
    },
  ];

  return (
    <div className="tw-flex tw-flex-col tw-w-full tw-h-full">
      <Box
        sx={{
          padding: "1em",
        }}
      >
        <div className="tw-p-4 tw-bg-white tw-rounded-lg tw-shadow-md">
          <h1 className="tw-mb-4 tw-color-primary">Plants</h1>
          <div className="tw-flex tw-mb-4 tw-h-[3.5vh]">
            <Input
              icon={FiSearch}
              containerStyle={{ marginRight: 4 }}
              id="search"
              type="text"
              name="search"
              placeholder="search"
              onChange={(e: any) => updateSearchQuery(e.target.value)}
              disabled={false}
              value={searchVal}
            />
          </div>
          {!isLoading && (
            <>
              <div className="tw-mb-4" onContextMenu={handleContextMenu}>
                {!isMobile ? (
                  <>
                    <AgGridReact
                      className="ag-theme-balham"
                      domLayout="autoHeight"
                      columnDefs={columnDefs}
                      rowHeight={40}
                      rowData={plants}
                      gridOptions={{}}
                      rowSelection="multiple"
                      defaultColDef={defaultColDef}
                      onGridReady={onGridReady}
                      onCellContextMenu={handleCellContextMenu}
                      colResizeDefault="shift"
                      animateRows
                      pagination
                      pivotPanelShow="always"
                      paginationPageSize={pageSize}
                    />
                    {ContextMenuComponent}
                  </>
                ) : (
                  <PlantListMobile
                    plants={plants}
                    searchVal={searchVal}
                    handleEdit={handleEdit}
                  />
                )}
              </div>
              {showEditDialog && (
                <PlantCreateEdit
                  id={plant_id}
                  selectedPlant={selectedPlant!}
                  isOpen={showEditDialog}
                  onClose={handleClose}
                  roleGroups={roleGroups}
                  engagementStatuses={engagementStatuses}
                  companies={companies}
                  clients={clients}
                />
              )}
            </>
          )}
        </div>
      </Box>
    </div>
  );
}
